import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

const MONTHS = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, "0"));

const validDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatValue = (value) => {
    const date = validDate(value);
    if (!date) return "Выбрать дату и время";

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const sameDay = (a, b) => (
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
);

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const shiftMonth = (date, amount) => (
    new Date(date.getFullYear(), date.getMonth() + amount, 1)
);

const buildDays = (monthDate) => {
    const first = startOfMonth(monthDate);
    const weekday = (first.getDay() + 6) % 7;
    const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - weekday);

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(
            gridStart.getFullYear(),
            gridStart.getMonth(),
            gridStart.getDate() + index
        );

        return {
            date,
            currentMonth: date.getMonth() === monthDate.getMonth(),
        };
    });
};

export default function TaskDueDatePicker({
    value = "",
    onChange,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(() => startOfMonth(validDate(value) || new Date()));
    const [selected, setSelected] = useState(() => validDate(value) || null);
    const [hour, setHour] = useState(() => {
        const date = validDate(value);
        return String(date?.getHours?.() ?? 18).padStart(2, "0");
    });
    const [minute, setMinute] = useState(() => {
        const date = validDate(value);
        const raw = date?.getMinutes?.() ?? 0;
        const rounded = Math.min(55, Math.round(raw / 5) * 5);
        return String(rounded).padStart(2, "0");
    });

    const days = useMemo(() => buildDays(month), [month]);
    const today = useMemo(() => new Date(), []);

    useEffect(() => {
        if (!open) return;

        const date = validDate(value);
        const base = date || new Date();

        setMonth(startOfMonth(base));
        setSelected(date);
        setHour(String(date?.getHours?.() ?? 18).padStart(2, "0"));

        const rawMinute = date?.getMinutes?.() ?? 0;
        const rounded = Math.min(55, Math.round(rawMinute / 5) * 5);
        setMinute(String(rounded).padStart(2, "0"));
    }, [open, value]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                setOpen(false);
            }
        };

        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [open]);

    const chooseToday = () => {
        const now = new Date();
        setSelected(now);
        setMonth(startOfMonth(now));
    };

    const apply = () => {
        if (!selected) return;

        const next = new Date(
            selected.getFullYear(),
            selected.getMonth(),
            selected.getDate(),
            Number(hour) || 0,
            Number(minute) || 0,
            0,
            0
        );

        onChange?.(next.toISOString());
        setOpen(false);
    };

    const clear = () => {
        onChange?.("");
        setSelected(null);
        setOpen(false);
    };

    const picker = open && typeof document !== "undefined"
        ? createPortal(
            <div
                className="office-task-date-picker-AO"
                role="dialog"
                aria-modal="true"
                aria-label="Выбор срока задания"
            >
                <button
                    type="button"
                    className="office-task-date-picker-AO__backdrop"
                    onClick={() => setOpen(false)}
                    aria-label="Закрыть календарь"
                />

                <section className="office-task-date-picker-AO__panel">
                    <header className="office-task-date-picker-AO__head">
                        <div>
                            <strong>Срок задания</strong>
                            <span>Выберите дату и время</span>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
                    </header>

                    <div className="office-task-date-picker-AO__month-nav">
                        <button type="button" onClick={() => setMonth((current) => shiftMonth(current, -1))}>
                            <ChevronLeftRoundedIcon />
                        </button>
                        <strong>{MONTHS[month.getMonth()]} {month.getFullYear()}</strong>
                        <button type="button" onClick={() => setMonth((current) => shiftMonth(current, 1))}>
                            <ChevronRightRoundedIcon />
                        </button>
                    </div>

                    <div className="office-task-date-picker-AO__weekdays">
                        {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
                    </div>

                    <div className="office-task-date-picker-AO__days">
                        {days.map(({ date, currentMonth }) => (
                            <button
                                type="button"
                                key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                                className={[
                                    currentMonth ? "" : "is-outside",
                                    sameDay(date, today) ? "is-today" : "",
                                    sameDay(date, selected) ? "is-selected" : "",
                                ].filter(Boolean).join(" ")}
                                onClick={() => {
                                    setSelected(date);
                                    if (!currentMonth) setMonth(startOfMonth(date));
                                }}
                            >
                                {date.getDate()}
                            </button>
                        ))}
                    </div>

                    <div className="office-task-date-picker-AO__time">
                        <div>
                            <AccessTimeRoundedIcon sx={{ fontSize: 18 }} />
                            <span>Время</span>
                        </div>

                        <select value={hour} onChange={(event) => setHour(event.target.value)}>
                            {HOURS.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <b>:</b>
                        <select value={minute} onChange={(event) => setMinute(event.target.value)}>
                            {MINUTES.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                    </div>

                    <footer className="office-task-date-picker-AO__actions">
                        <button type="button" className="is-ghost" onClick={clear}>Без срока</button>
                        <button type="button" className="is-ghost" onClick={chooseToday}>Сегодня</button>
                        <button type="button" className="is-primary" onClick={apply} disabled={!selected}>
                            Выбрать
                        </button>
                    </footer>
                </section>
            </div>,
            document.body
        )
        : null;

    return (
        <>
            <button
                type="button"
                className={[
                    "office-task-date-picker-AO__trigger",
                    value ? "has-value" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => !disabled && setOpen(true)}
                disabled={disabled}
            >
                <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
                <span>{formatValue(value)}</span>
            </button>
            {picker}
        </>
    );
}
